/**
 * Writes a real .xlsx and hands it to the browser as a download.
 *
 * Real xlsx rather than a CSV named .xlsx, for one concrete reason: volsers. Tape barcodes are
 * alphanumeric and routinely look like numbers or dates — '000123', '1E5', 'MAR93' — and Excel rewrites
 * every one of them when it parses a CSV, silently, in the column that identifies the cartridge. A
 * workbook states each cell's type, so a volser arrives as the volser. The same goes for data volumes
 * and dates, which are pre-formatted strings and must not be re-interpreted.
 *
 * exceljs is loaded with a dynamic import so it is code-split out of the main bundle: it is a megabyte
 * of library serving one button, and nobody who never clicks Export should download it.
 */

/** Header row styling, kept here so both sheets of a workbook look like one document. */
const HEADER_FILL = 'FF3A3E3F';
const HEADER_FONT_COLOUR = 'FFFFFFFF';

/**
 * Column width from the widest value in it, so a report opens readable instead of as a wall of ####.
 *
 * Capped, because Comments carries free-text operator notes that can run to hundreds of characters and
 * one column three screens wide makes the sheet unusable. Excel wraps or clips beyond the cap, which is
 * the reader's choice to make, not ours.
 */
const MIN_COLUMN_WIDTH = 10;
const MAX_COLUMN_WIDTH = 40;

function columnWidths(headers, rows) {
    return headers.map((header, index) => {
        const longest = rows.reduce(
            (widest, row) => Math.max(widest, String(row?.[index] ?? '').length),
            String(header ?? '').length,
        );
        return Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, longest + 2));
    });
}

/**
 * Builds a workbook from plain sheet descriptions and downloads it.
 *
 * @param {Array<{name: string, headers: string[], rows: Array<Array<string|number>>, text?: boolean}>} sheets
 *        `text: true` pins every data cell to Excel's text format, so values survive both the initial
 *        read and a later edit of the cell. Used for the media sheet; the summary sheet holds counts
 *        that should stay addable.
 * @param {string} fileName - Suggested name for the download.
 * @returns {Promise<void>} Resolves once the download has been handed to the browser.
 */
export async function downloadWorkbook(sheets, fileName) {
    const ExcelJS = await import('exceljs');
    // Interop: the dynamic import gives a module namespace, whose default is the library under CRA's
    // CommonJS interop and whose own properties are the library when bundled as ESM.
    const {Workbook} = ExcelJS.default ?? ExcelJS;

    const workbook = new Workbook();
    workbook.creator = 'Tape Ark Nexus';
    workbook.created = new Date();

    sheets.forEach(({name, headers, rows, text = false}) => {
        const sheet = workbook.addWorksheet(name);

        const headerRow = sheet.addRow(headers);
        headerRow.font = {bold: true, color: {argb: HEADER_FONT_COLOUR}};
        headerRow.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: HEADER_FILL}};

        rows.forEach(row => {
            const added = sheet.addRow(row);
            // '@' is Excel's text format. Strings already round-trip as text; this additionally stops
            // Excel from converting the cell the moment someone clicks into it and presses Enter.
            if (text) added.eachCell(cell => { cell.numFmt = '@'; });
        });

        // Headers stay visible while scrolling ten thousand rows, and filter dropdowns let a reader
        // narrow by status or tape type without writing a formula — the first thing anyone does with
        // a media report.
        sheet.views = [{state: 'frozen', ySplit: 1}];
        if (rows.length > 0) {
            sheet.autoFilter = {
                from: {row: 1, column: 1},
                to: {row: rows.length + 1, column: headers.length},
            };
        }
        columnWidths(headers, rows).forEach((width, index) => { sheet.getColumn(index + 1).width = width; });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveBlob(new Blob([buffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}), fileName);
}

/**
 * Hands a blob to the browser as a named download.
 *
 * Same anchor-and-revoke dance as the job documents download in jobDocuments.jsx. The object URL is
 * revoked immediately after the synthetic click, which is safe: the browser has already taken its own
 * reference to the blob by then, and leaving it un-revoked pins the whole workbook in memory for the
 * lifetime of the tab.
 */
function saveBlob(blob, fileName) {
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(blobUrl);
}
